import * as React from 'react';

/**
 * Focus-trap hook for hand-rolled modal overlays.
 *
 * Radix-based primitives (Dialog, AlertDialog, Popover, DropdownMenu, Select)
 * already trap focus correctly. This hook is for the small handful of bespoke
 * overlays in `@tyndo/ui-kit/domain` that pre-date the Radix migration —
 * `ConsoleShell` command palette + confirm modal, `WizardShell` cancel-reason
 * modal, etc — so they get the same keyboard behaviour without forcing a
 * Radix dependency on every consumer.
 *
 * What it does (WCAG 2.1.2 — No Keyboard Trap, 2.4.3 — Focus Order):
 *
 *  1. Captures the previously-focused element on mount and restores it on
 *     unmount. Operators tabbing back from a modal return to the trigger.
 *  2. Moves focus into the trapped container on mount. The first focusable
 *     descendant is selected unless an explicit `initialFocusRef` is provided.
 *  3. Listens for `Tab` / `Shift+Tab` and clamps focus to the first/last
 *     focusable descendants so screen-reader users cannot escape the modal
 *     by tabbing into the underlying page.
 *  4. Optionally invokes `onEscape` when the operator hits `Esc`.
 *
 * Usage:
 *
 *   const trapRef = useFocusTrap<HTMLDivElement>({ active: open, onEscape });
 *   return open ? <div ref={trapRef} role="dialog" aria-modal="true">…</div> : null;
 */
export interface UseFocusTrapOptions {
  /** When false the trap is detached. Defaults to true. */
  active?: boolean;
  /** Called when the operator presses Escape inside the trap. */
  onEscape?: () => void;
  /**
   * Optional ref to focus first. When omitted the first focusable descendant
   * of the trap container is focused.
   */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useFocusTrap<T extends HTMLElement>(
  options: UseFocusTrapOptions = {}
): React.RefObject<T | null> {
  const { active = true, onEscape, initialFocusRef } = options;
  const containerRef = React.useRef<T | null>(null);

  React.useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = typeof document !== 'undefined'
      ? (document.activeElement as HTMLElement | null)
      : null;

    function getFocusable(): HTMLElement[] {
      if (!container) return [];
      return Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
    }

    // Initial focus — explicit ref, else first focusable, else the container.
    const target = initialFocusRef?.current ?? getFocusable()[0] ?? container;
    // Make container itself focusable for screen-reader announcement when
    // there are no focusable descendants.
    if (target === container && !container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && onEscape) {
        e.stopPropagation();
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !container?.contains(active)) {
          e.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus({ preventScroll: true });
        }
      }
    }

    container.addEventListener('keydown', handleKey);
    return () => {
      container.removeEventListener('keydown', handleKey);
      // Restore focus to the previously-focused element if it's still in DOM.
      if (previouslyFocused && typeof document !== 'undefined' && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [active, onEscape, initialFocusRef]);

  return containerRef;
}
